import Link from 'next/link'
import React, { useContext, useState } from 'react'

import { focus } from 'lib/ugc-interact'
import { ArticleCardContext } from 'Components/ugc/article-card'
import { sendTeaCommEvent } from 'lib/tea-analyze'
import Toast from 'Components/toast-messager'
import { GlobalStoreContext } from 'Components/store'
import { get } from 'lodash'
import Button from 'Components/button'
import classNames from 'classnames'
import useLoginCheckCallback from 'Components/hooks/use-login-check-callback'

export interface IUser {
  user_id: number
  name: string
  avatar_url: string
  description: string
  follow: boolean
  user_verified: boolean
  verified_content: string
  motor_auth_show_info: {
    car_identity_desc: string
    auth_v_desc: string
    auth_v_type: number
  }
  standard_user_info: string
}

interface IProps {
  info: IUser
}

const vip_1_icon = require('static/yellow-v.png')
const vip_2_icon = require('static/v_blue_24.png')
const plus_icon = require('static/plus.png')

const handleClickFocus = (
  user_id: number,
  to_user_id: number,
  setFocused: (b: boolean) => void
) => {
  setFocused(true)
  focus({ user_id, to_user_id }).catch((e) => {
    console.error(e)
    setFocused(false)
  })
}

/* eslint-disable complexity */
const User = ({ info }: IProps) => {
  const {
    user_id,
    name,
    avatar_url,
    description,
    verified_content,
    motor_auth_show_info,
    user_verified,
    follow,
    standard_user_info,
  } = info || {}
  const { auth_v_desc, auth_v_type } = motor_auth_show_info || {}
  const desc = description || verified_content || auth_v_desc
  const [focused, setFocused] = useState(false)
  const { inheritNewCard, group_id } = useContext(ArticleCardContext)
  const { state: globalState } = useContext(GlobalStoreContext)

  let standardUserInfo = null
  try {
    standardUserInfo = JSON.parse(standard_user_info)
    if (typeof standardUserInfo !== 'object') {
      throw new Error('standard_user_info 不是JSON对象')
    }
  } catch (e) {
    console.error(e)
  }
  const { desc_content: badge_content, pic_url: badge_icon } =
    standardUserInfo?.medal_list?.[0] || {}
  const { description: brand_content, icon: brand_icon } =
    standardUserInfo?.feed_user_description || {}

  const doFocus = useLoginCheckCallback(
    () => {
      inheritNewCard &&
        sendTeaCommEvent('rt_follow', {
          author_id: user_id,
          group_id,
        })
      const user_id_login = get(globalState, 'userData.user_id')
      if (!user_id_login) {
        Toast.show({
          text: '请先登录~', // 1
        })
        return
      }
      handleClickFocus(user_id, user_id, setFocused)
    },
    {
      enter_method: 'follow',
    }
  )

  const handleFocus = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    doFocus()
  }

  return (
    <div className="ugc-user">
      <a
        // eslint-disable-next-line react/jsx-no-target-blank
        target="_blank"
        href={`/user/ugc_profile/${user_id}`}
      >
        <div
          className={classNames('avatar', {
            'vip-1': user_verified && auth_v_type === 1,
            'vip-2': user_verified && auth_v_type === 2,
          })}
          style={{
            backgroundImage: `url(${avatar_url})`,
          }}
          onClick={() => {
            if (inheritNewCard) {
              sendTeaCommEvent('click_forum_feed_profile', {
                author_id: user_id,
                group_id,
              })
            }
          }}
        />
      </a>
      <a
        // eslint-disable-next-line react/jsx-no-target-blank
        target="_blank"
        href={`/user/ugc_profile/${user_id}`}
      >
        <div className="name-container">
          <span className="name-main">
            <span className="name">{name}</span>
            {badge_content && (
              <span className="badge">
                {badge_icon && (
                  <span
                    className="badge-icon"
                    style={{ backgroundImage: `url("${badge_icon}")` }}
                  />
                )}
                <span className="badge-text">{badge_content}</span>
              </span>
            )}
          </span>
          {(brand_content || follow) && (
            <span className="desc">
              {follow && `已关注`}
              {follow && brand_content && ' ·\u00A0'}
              {brand_icon && (
                <span
                  className="desc-icon"
                  style={{ backgroundImage: `url("${brand_icon}")` }}
                />
              )}
              {brand_content}
            </span>
          )}
        </div>
      </a>
      {!follow && String(user_id) !== globalState?.userData?.user_id && (
        <Button
          type="new_primary"
          className={focused ? 'followed' : 'follow'}
          onClick={handleFocus}
        >
          {focused ? '已' : <span />}关注
        </Button>
      )}

      <style jsx>
        {`
          .ugc-user {
            position: relative;
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            height: 52px;
          }
          .avatar {
            position: relative;
            width: 52px;
            height: 52px;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            border-radius: 50%;
            cursor: pointer;
            background-clip: content-box;
            border: solid 0.5px #e6e8f2;
          }
          .vip-1::after,
          .vip-2::after {
            content: '';
            height: 16px;
            width: 16px;
            position: absolute;
            background-repeat: no-repeat;
            background-position: center;
            background-size: cover;
            border-radius: 50%;
            right: 0;
            bottom: 0;
          }
          .vip-1::after {
            background-image: url('${vip_1_icon}');
          }
          .vip-2::after {
            background-image: url('${vip_2_icon}');
          }

          .name-container {
            display: inline-flex;
            flex-direction: column;
            justify-content: center;
            padding-left: 8px;
          }
          .name-main {
            display: inline-flex;
            justify-content: flex-start;
            align-items: center;
            height: 20px;
          }
          .name {
            font-size: 14px;
            font-weight: 500;
            line-height: 20px;
            color: #1f2129;
          }
          /* 兼容chrome最小字体是12   对所有数字进行了等比缩放。 */
          .badge {
            margin-left: 8px;
            display: inline-flex;
            position: relative;
            align-items: center;
            border-radius: 10.67px;
            background-color: rgba(169, 169, 229, 0.2);
            height: 21.33px;
            transform: scale(0.75) translateX(-12.5%);
            padding-right: 10.67px;
            box-sizing: border-box;
          }
          .badge-icon {
            position: absolute;
            width: 21.33px;
            height: 21.33px;
            background-repeat: no-repeat;
            background-position: center;
            background-size: cover;
            border-radius: 50%;
          }
          .badge-text {
            box-sizing: border-box;
            padding-left: 26.67px;
            font-size: 12px;
            font-weight: 500;
            color: #3d3c67;
            line-height: 13.33px;
          }
          .desc {
            display: inline-flex;
            align-items: center;
            padding-top: 6px;
            font-size: 12px;
            line-height: 18px;
            color: #606370;
          }

          .desc-icon {
            width: 16px;
            height: 16px;
            margin-right: 2px;
            background-repeat: no-repeat;
            background-position: center;
            background-size: cover;
            border-radius: 50%;
          }

          :global(.ugc-user .follow, .ugc-user .followed) {
            width: 70px;
            height: 28px;
            padding: 4px 10px;
            border: 0;
            outline: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 14px;
            font-weight: 500;
            line-height: 20px;
            margin-left: auto;
            font-family: PingFang SC;
            border-radius: 2px;
            box-sizing: border-box;
            border: solid 1px #ffcd32;
            font-weight: normal;
          }
          :global(.ugc-user .follow) {
            color: #1f2129;
            background-color: rgba(255, 204, 50, 0.12);
            cursor: pointer;
          }
          :global(.ugc-user .follow:hover) {
            color: #1f2129;
            background-color: rgba(255, 204, 50, 0.24);
            cursor: pointer;
          }

          :global(.ugc-user .followed) {
            background-color: white;
            border: solid 1px #c9cbd6;
            color: #979aa8;
          }

          :global(.ugc-user .followed:hover) {
            background-color: white;
            color: #979aa8;
          }

          :global(.ugc-user .follow span) {
            background-image: url('${plus_icon}');
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            width: 16px;
            height: 16px;
            margin-right: 2px;
          }
        `}
      </style>
    </div>
  )
}

export default User
